import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Space, 
  Tag, 
  Divider, 
  Spin,
  Statistic,
  Progress,
  List,
  Avatar,
  Badge,
  Tooltip,
  Tabs,
  Collapse,
  Timeline,
  Radio,
  Select,
  Table
} from 'antd';
import { 
  ExclamationCircleOutlined,
  SyncOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  LockOutlined,
  LineChartOutlined,
  SettingOutlined,
  BellOutlined,
  InfoCircleOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  ShopOutlined,
  SoundOutlined,
  TagOutlined
} from '@ant-design/icons';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import type { TabsProps } from 'antd';
import dayjs from 'dayjs';

// Import echarts to create charts
import ReactECharts from 'echarts-for-react';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface InventoryData {
  date: string;
  available: number;
  booked: number;
  total: number;
  isWarningDay?: boolean;
  isNotificationDay?: boolean;
  // 添加上周同期和去年今日数据
  lastWeek?: {
    available: number;
    booked: number;
  };
  lastYear?: {
    available: number;
    booked: number;
  };
}

interface ChannelData {
  channel: string;
  booked: number;
  availableToSell: number;
  icon: string;
  color: string;
}

interface OrderData {
  id: string;
  customerName: string;
  channel: string;
  roomCount: number;
  checkIn: string;
  checkOut: string;
  status: string;
}

interface ActionLogItem {
  time: string;
  action: string;
  user: string;
  result: string;
}

interface AlertData {
  id: string;
  roomType: string;
  date: string;
  severity: 'high' | 'medium' | 'low';
  remainingInventory: number;
  totalInventory: number;
  bookedInventory: number;
  notificationTime: string;
  storeName: string;
  lastUpdated: string;
  recommendedAction: string;
  recommendationReason: string;
  notificationContent: string;
  comparisonData: {
    lastWeek: {
      remainingInventory: number;
      bookedInventory: number;
    };
    competitorAverage: {
      remainingInventory: number;
      bookedInventory: number;
    };
  };
  inventoryData: InventoryData[];
  channelData: ChannelData[];
  orders: OrderData[];
  competitorData: {
    name: string;
    remainingRooms: number;
    price: number;
    lastUpdated: string;
    roomType?: string;
  }[];
  actionLogs: ActionLogItem[];
  // 历史库存趋势数据
  historicalTrend: {
    // 历史消耗数据（过去7天、去年同期、上周同日）
    consumptionData: {
      date: string;
      current: number; // 当前消耗
      lastYear: number; // 去年同期
      lastWeek: number; // 上周同日
    }[];
    // 售罄速度数据
    selloutSpeed: {
      name: string; // 对比项名称
      days: number; // 售罄天数
      hours: number; // 售罄小时数
      color: string; // 颜色
    }[];
  };
  // 可选房型列表
  availableRoomTypes: {
    id: string;
    name: string;
    isWarning: boolean;
  }[];
  // 修改远期预订分析数据
  futureBookingData: {
    date: string; // 日期
    bookingCount: number; // 预订数量
    price: number; // 房价
    competitorAvgPrice?: number; // 竞争对手平均价格（可选）
    isNotificationDay?: boolean; // 是否为通知日
    isWarningDay?: boolean; // 是否为预警日
    roomType?: string; // 添加房型字段
  }[];
  // 添加事件信息数据
  events: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    type: 'holiday' | 'exhibition' | 'festival' | 'other';
    impact: 'high' | 'medium' | 'low'; // 影响程度
  }[];
}

const InventoryAlertAnalysis: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alertData, setAlertData] = useState<AlertData | null>(null);
  const [selectedRoomType, setSelectedRoomType] = useState<string>('');
  const [competitorRoomType, setCompetitorRoomType] = useState<string>('');
  const [bookingRoomType, setBookingRoomType] = useState<string>(''); // 增加预订分析房型选择状态
  const notificationData = location.state?.notificationData || null;

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const mockData: AlertData = {
        id: id || '1',
        roomType: notificationData?.roomType || '豪华海景房',
        date: notificationData?.date || '2023-07-01',
        severity: notificationData?.severity || 'high',
        remainingInventory: notificationData?.remainingInventory || 2,
        totalInventory: 50,
        bookedInventory: 48,
        notificationTime: '2023-06-29 09:15',
        storeName: '三元桥店',
        lastUpdated: '2023-06-29 14:30',
        recommendedAction: '建议立即上调2025-07-01底价至¥1280',
        recommendationReason: '●对比上周同期消耗增加20% + 竞品均价上涨6%\n●米漾酒店（深圳会展中心岗厦地铁站店）同房型2025-04-01剩余1间且价格已调至¥880（本店现价¥799）。',
        notificationContent: '【2023-07-01】豪华海景房库存剩余2间',
        
        // 增加对比数据
        comparisonData: {
          lastWeek: {
            remainingInventory: 6,
            bookedInventory: 35
          },
          competitorAverage: {
            remainingInventory: 5,
            bookedInventory: 42
          }
        },
        
        // 未来库存分布数据 - 修改为包含通知日、预警日和预警日后多天，并增加上周同期和去年同期数据
        inventoryData: [
          { 
            date: '2023-06-29', 
            available: 8, 
            booked: 12, 
            total: 20, 
            isNotificationDay: true,
            lastWeek: { available: 10, booked: 10 },
            lastYear: { available: 12, booked: 8 }
          },
          { 
            date: '2023-06-30', 
            available: 5, 
            booked: 15, 
            total: 20, 
            isNotificationDay: false,
            lastWeek: { available: 8, booked: 12 },
            lastYear: { available: 9, booked: 11 }
          },
          { 
            date: '2023-07-01', 
            available: 1.8, 
            booked: 18.2, 
            total: 20, 
            isWarningDay: true,
            lastWeek: { available: 5, booked: 15 },
            lastYear: { available: 6, booked: 14 }
          },
          { 
            date: '2023-07-02', 
            available: 3, 
            booked: 17, 
            total: 20, 
            isNotificationDay: false,
            lastWeek: { available: 6, booked: 14 },
            lastYear: { available: 5, booked: 15 }
          },
          { 
            date: '2023-07-03', 
            available: 5, 
            booked: 15, 
            total: 20, 
            isNotificationDay: false,
            lastWeek: { available: 7, booked: 13 },
            lastYear: { available: 4, booked: 16 }
          },
          { 
            date: '2023-07-04', 
            available: 7, 
            booked: 13, 
            total: 20, 
            isNotificationDay: false,
            lastWeek: { available: 9, booked: 11 },
            lastYear: { available: 7, booked: 13 }
          },
          { 
            date: '2023-07-05', 
            available: 9, 
            booked: 11, 
            total: 20, 
            isNotificationDay: false,
            lastWeek: { available: 11, booked: 9 },
            lastYear: { available: 10, booked: 10 }
          },
        ],
        
        // 各渠道分布数据
        channelData: [
          { 
            channel: 'OTA', 
            booked: 8, 
            availableToSell: 0.8, 
            icon: 'https://img.meituan.net/joymerchant/9963a65f9cf211.1.jpg', 
            color: '#FFD100' 
          },
          { 
            channel: '散客', 
            booked: 5, 
            availableToSell: 0.5, 
            icon: 'https://pages.c-ctrip.com/commerce/place/images/ctrip_logo.png', 
            color: '#2577E3' 
          },
          { 
            channel: '协议单位', 
            booked: 3.5, 
            availableToSell: 0.3, 
            icon: 'https://gw.alicdn.com/tfs/TB1vWvWaZfrK1RkSmLyXXcGApXa-225-225.png', 
            color: '#FF7300' 
          },
          { 
            channel: '会员', 
            booked: 1.7, 
            availableToSell: 0.2, 
            icon: 'https://t7.baidu.com/it/u=1956604245,3662848045&fm=193&f=GIF', 
            color: '#529C8A' 
          },
        ],
        
        // 在手订单数据
        orders: [
          { 
            id: 'ORD2023063001', 
            customerName: '张先生', 
            channel: '美团', 
            roomCount: 1, 
            checkIn: '2023-07-01', 
            checkOut: '2023-07-03', 
            status: '已确认' 
          },
          { 
            id: 'ORD2023062902', 
            customerName: '李先生', 
            channel: '携程', 
            roomCount: 2, 
            checkIn: '2023-07-01', 
            checkOut: '2023-07-02', 
            status: '已确认' 
          },
          { 
            id: 'ORD2023062803', 
            customerName: '王女士', 
            channel: '自有渠道', 
            roomCount: 1, 
            checkIn: '2023-07-01', 
            checkOut: '2023-07-04', 
            status: '已支付' 
          },
        ],
        
        // 竞品数据
        competitorData: [
          { 
            name: '三亚海棠湾喜来登度假酒店', 
            remainingRooms: 3, 
            price: 1580, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '豪华海景房'
          },
          { 
            name: '三亚亚特兰蒂斯酒店', 
            remainingRooms: 2, 
            price: 1680, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '豪华海景房'
          },
          { 
            name: '三亚海棠湾仁恒皇冠假日酒店', 
            remainingRooms: 5, 
            price: 1380, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '豪华海景房'
          },
          { 
            name: '三亚海棠湾喜来登度假酒店', 
            remainingRooms: 7, 
            price: 1280, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '高级双床房'
          },
          { 
            name: '三亚亚特兰蒂斯酒店', 
            remainingRooms: 8, 
            price: 1380, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '高级双床房'
          },
          { 
            name: '三亚海棠湾仁恒皇冠假日酒店', 
            remainingRooms: 9, 
            price: 1180, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '高级双床房'
          },
          { 
            name: '三亚海棠湾喜来登度假酒店', 
            remainingRooms: 4, 
            price: 1880, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '行政套房'
          },
          { 
            name: '三亚亚特兰蒂斯酒店', 
            remainingRooms: 3, 
            price: 1980, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '行政套房'
          },
          { 
            name: '三亚海棠湾仁恒皇冠假日酒店', 
            remainingRooms: 6, 
            price: 1780, 
            lastUpdated: '2023-06-29 12:00',
            roomType: '行政套房'
          },
        ],
        
        // 操作日志
        actionLogs: [
          { 
            time: '2023-06-28 10:30', 
            action: '系统自动预警', 
            user: '系统', 
            result: '生成预警通知' 
          },
          { 
            time: '2023-06-28 11:15', 
            action: '调整底价', 
            user: '张经理', 
            result: '提高底价至¥1180' 
          },
          { 
            time: '2023-06-28 14:30', 
            action: '锁定渠道库存', 
            user: '李总监', 
            result: '美团渠道锁量50%' 
          },
        ],
        
        // 历史库存趋势数据
        historicalTrend: {
          // 历史消耗数据
          consumptionData: [
            { date: '2023-06-23', current: 16, lastYear: 13, lastWeek: 14 },
            { date: '2023-06-24', current: 18, lastYear: 12, lastWeek: 15 },
            { date: '2023-06-25', current: 14, lastYear: 14, lastWeek: 13 },
            { date: '2023-06-26', current: 19, lastYear: 16, lastWeek: 17 },
            { date: '2023-06-27', current: 22, lastYear: 18, lastWeek: 19 },
            { date: '2023-06-28', current: 25, lastYear: 19, lastWeek: 20 },
            { date: '2023-06-29', current: 27, lastYear: 21, lastWeek: 23 },
          ],
          // 售罄速度数据
          selloutSpeed: [
            { name: '去年同期', days: 2, hours: 12, color: '#8884d8' },
            { name: '上周同日', days: 3, hours: 6, color: '#82ca9d' },
            { name: '本次预测', days: 1, hours: 18, color: '#ff7300' },
          ]
        },
        // 可选房型列表
        availableRoomTypes: [
          { id: '1', name: '豪华海景房', isWarning: true },
          { id: '2', name: '商务套房', isWarning: false },
          { id: '3', name: '高级双床房', isWarning: false },
          { id: '4', name: '行政套房', isWarning: false },
        ],
        // 修改远期预订分析数据，添加房型信息
        futureBookingData: [
          { 
            date: '2023-06-29', 
            bookingCount: 12, 
            price: 799, 
            competitorAvgPrice: 850,
            isNotificationDay: true,
            roomType: '豪华海景房'
          },
          { 
            date: '2023-06-30', 
            bookingCount: 16, 
            price: 899, 
            competitorAvgPrice: 920,
            roomType: '豪华海景房'
          },
          { 
            date: '2023-07-01', 
            bookingCount: 22, 
            price: 999, 
            competitorAvgPrice: 1050,
            isWarningDay: true,
            roomType: '豪华海景房'
          },
          { 
            date: '2023-07-02', 
            bookingCount: 18, 
            price: 999, 
            competitorAvgPrice: 1000,
            roomType: '豪华海景房'
          },
          { 
            date: '2023-07-03', 
            bookingCount: 15, 
            price: 899, 
            competitorAvgPrice: 920,
            roomType: '豪华海景房'
          },
          // 高级双床房数据
          { 
            date: '2023-06-29', 
            bookingCount: 10, 
            price: 699, 
            competitorAvgPrice: 750,
            isNotificationDay: true,
            roomType: '高级双床房'
          },
          { 
            date: '2023-06-30', 
            bookingCount: 13, 
            price: 799, 
            competitorAvgPrice: 820,
            roomType: '高级双床房'
          },
          { 
            date: '2023-07-01', 
            bookingCount: 17, 
            price: 899, 
            competitorAvgPrice: 950,
            isWarningDay: true,
            roomType: '高级双床房'
          },
          { 
            date: '2023-07-02', 
            bookingCount: 14, 
            price: 899, 
            competitorAvgPrice: 900,
            roomType: '高级双床房'
          },
          { 
            date: '2023-07-03', 
            bookingCount: 11, 
            price: 799, 
            competitorAvgPrice: 820,
            roomType: '高级双床房'
          },
          // 行政套房数据
          { 
            date: '2023-06-29', 
            bookingCount: 8, 
            price: 1099, 
            competitorAvgPrice: 1150,
            isNotificationDay: true,
            roomType: '行政套房'
          },
          { 
            date: '2023-06-30', 
            bookingCount: 10, 
            price: 1199, 
            competitorAvgPrice: 1220,
            roomType: '行政套房'
          },
          { 
            date: '2023-07-01', 
            bookingCount: 14, 
            price: 1299, 
            competitorAvgPrice: 1350,
            isWarningDay: true,
            roomType: '行政套房'
          },
          { 
            date: '2023-07-02', 
            bookingCount: 12, 
            price: 1299, 
            competitorAvgPrice: 1300,
            roomType: '行政套房'
          },
          { 
            date: '2023-07-03', 
            bookingCount: 9, 
            price: 1199, 
            competitorAvgPrice: 1220,
            roomType: '行政套房'
          }
        ],
        // 添加事件信息数据
        events: [
          {
            id: '1',
            name: '端午节假期',
            startDate: '2023-06-22',
            endDate: '2023-06-24',
            type: 'holiday',
            impact: 'high'
          },
          {
            id: '2',
            name: '全球电子商务博览会',
            startDate: '2023-06-28',
            endDate: '2023-07-02',
            type: 'exhibition',
            impact: 'high'
          },
          {
            id: '3',
            name: '国际音乐节',
            startDate: '2023-07-08',
            endDate: '2023-07-10',
            type: 'festival',
            impact: 'medium'
          },
          {
            id: '4',
            name: '暑期旅游高峰',
            startDate: '2023-07-15',
            endDate: '2023-08-15',
            type: 'other',
            impact: 'high'
          },
          {
            id: '5',
            name: '国际青年论坛',
            startDate: '2023-06-15',
            endDate: '2023-06-18',
            type: 'exhibition',
            impact: 'medium'
          }
        ]
      };
      
      setAlertData(mockData);
      // 设置默认选中预警房型
      setSelectedRoomType(mockData.availableRoomTypes.find(rt => rt.isWarning)?.id || mockData.availableRoomTypes[0].id);
      // 设置默认选中预警房型作为竞品分析筛选条件
      setCompetitorRoomType(mockData.roomType);
      // 设置默认选中预警房型作为预订分析筛选条件
      setBookingRoomType(mockData.roomType);
      setLoading(false);
    }, 1500);
  }, [id, notificationData]);

  const handleRefresh = () => {
    setRefreshing(true);
    // Simulate API refresh
    setTimeout(() => {
      setRefreshing(false);
    }, 1000);
  };

  const handleGoToSettings = () => {
    // Navigate to inventory settings page
    navigate('/mobile-preview/inventory-settings');
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return '#FF4D4F';
      case 'medium':
        return '#FAAD14';
      case 'low':
        return '#52C41A';
      default:
        return '#1890FF';
    }
  };
  
  const getInventoryOptions = () => {
    if (!alertData) return {};
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['当期可售', '上周同期', '去年今日'],
        bottom: 0,
        textStyle: {
          fontSize: 10
        },
        itemWidth: 10,
        itemHeight: 10
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 60,
          zoomLock: false
        },
        {
          type: 'slider',
          show: false
        }
      ],
      xAxis: {
        type: 'category',
        data: alertData.inventoryData.map(item => {
          // 将日期格式化为 "MM-DD" 格式
          const date = new Date(item.date);
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        }),
        axisLabel: {
          fontSize: 11,
          interval: 0,
          formatter: function(value: string, index: number) {
            const item = alertData.inventoryData[index];
            if (item.isWarningDay) {
              return `{warningDay|${value}}`;
            } else if (item.isNotificationDay) {
              return `{notificationDay|${value}}`;
            }
            return value;
          },
          rich: {
            warningDay: {
              color: '#FF4D4F',
              fontWeight: 'bold',
              backgroundColor: '#FFF1F0',
              padding: [2, 4],
              borderRadius: 2
            },
            notificationDay: {
              color: '#1890FF',
              backgroundColor: '#E6F7FF',
              padding: [2, 4],
              borderRadius: 2
            }
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '房间数',
        nameTextStyle: {
          fontSize: 10
        },
        axisLabel: {
          fontSize: 10
        }
      },
      series: [
        {
          name: '当期可售',
          type: 'line',
          smooth: true,
          emphasis: {
            focus: 'series'
          },
          data: alertData.inventoryData.map((item, index) => {
            // 为预警日添加特殊样式
            if (item.isWarningDay) {
              return {
                value: item.available,
                itemStyle: {
                  color: '#73D13D',
                  borderWidth: 2,
                  borderColor: '#FF4D4F',
                  shadowBlur: 4,
                  shadowColor: 'rgba(255, 77, 79, 0.5)'
                }
              };
            }
            return item.available;
          }),
          itemStyle: {
            color: '#73D13D'
          },
          symbolSize: 6,
          lineStyle: {
            width: 2
          },
          markLine: {
            silent: true,
            data: [{
              xAxis: alertData.inventoryData.findIndex(item => item.isWarningDay),
              lineStyle: {
                color: '#FF4D4F',
                type: 'dashed'
              },
              label: {
                formatter: '预警日',
                position: 'insideStartTop',
                color: '#FF4D4F'
              }
            }]
          }
        },
        // 添加上周同期可售数据
        {
          name: '上周同期',
          type: 'line',
          smooth: true,
          emphasis: {
            focus: 'series'
          },
          data: alertData.inventoryData.map(item => item.lastWeek?.available),
          itemStyle: {
            color: '#8884d8'
          },
          symbolSize: 4,
          symbol: 'circle',
          lineStyle: {
            width: 1.5,
            type: 'dashed'
          }
        },
        // 添加去年今日可售数据
        {
          name: '去年今日',
          type: 'line',
          smooth: true,
          emphasis: {
            focus: 'series'
          },
          data: alertData.inventoryData.map(item => item.lastYear?.available),
          itemStyle: {
            color: '#82ca9d'
          },
          symbolSize: 4,
          symbol: 'triangle',
          lineStyle: {
            width: 1.5,
            type: 'dotted'
          }
        }
      ]
    };
  };
  
  // 获取筛选后的竞品数据
  const getFilteredCompetitorData = () => {
    if (!alertData) return [];
    
    if (competitorRoomType === '全部房型') {
      return alertData.competitorData;
    } else {
      return alertData.competitorData.filter(item => item.roomType === competitorRoomType);
    }
  };

  const getCompetitorOptions = () => {
    if (!alertData) return {};
    
    // 使用筛选后的竞品数据
    const filteredData = getFilteredCompetitorData();
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: ['本酒店', ...filteredData.map(item => item.name.substring(0, 4) + '...')],
        axisLabel: {
          interval: 0,
          rotate: 30
        }
      },
      yAxis: [
        {
          type: 'value',
          name: '价格(元)',
          position: 'left',
          axisLine: {
            lineStyle: {
              color: '#FF4D4F'
            }
          },
          axisLabel: {
            formatter: '{value}¥'
          }
        },
        {
          type: 'value',
          name: '剩余房间',
          position: 'right',
          axisLine: {
            lineStyle: {
              color: '#1890FF'
            }
          }
        }
      ],
      series: [
        {
          name: '价格',
          type: 'bar',
          yAxisIndex: 0,
          data: [1180, ...filteredData.map(item => item.price)],
          itemStyle: {
            color: '#FF4D4F'
          }
        },
        {
          name: '剩余房间',
          type: 'line',
          yAxisIndex: 1,
          data: [Math.round(alertData.remainingInventory), ...filteredData.map(item => item.remainingRooms)],
          itemStyle: {
            color: '#1890FF'
          }
        }
      ]
    };
  };

  const getHistoricalTrendOptions = () => {
    if (!alertData) return {};
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        }
      },
      legend: {
        data: ['当前消耗', '去年同期', '上周同日'],
        bottom: 0,
        textStyle: {
          fontSize: 11
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: alertData.historicalTrend.consumptionData.map(item => {
          const date = new Date(item.date);
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        }),
        axisLabel: {
          fontSize: 11,
          interval: 0,
          rotate: 30
        }
      },
      yAxis: {
        type: 'value',
        name: '消耗量',
        nameTextStyle: {
          fontSize: 10
        },
        axisLabel: {
          fontSize: 10
        }
      },
      series: [
        {
          name: '当前消耗',
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: {
            width: 2
          },
          symbolSize: 6,
          itemStyle: {
            color: '#FF4D4F'
          },
          emphasis: {
            focus: 'series'
          },
          data: alertData.historicalTrend.consumptionData.map(item => item.current)
        },
        {
          name: '去年同期',
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: {
            width: 2,
            type: 'dashed'
          },
          symbolSize: 6,
          itemStyle: {
            color: '#8884d8'
          },
          emphasis: {
            focus: 'series'
          },
          data: alertData.historicalTrend.consumptionData.map(item => item.lastYear)
        },
        {
          name: '上周同日',
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: {
            width: 2,
            type: 'dotted'
          },
          symbolSize: 6,
          itemStyle: {
            color: '#82ca9d'
          },
          emphasis: {
            focus: 'series'
          },
          data: alertData.historicalTrend.consumptionData.map(item => item.lastWeek)
        }
      ]
    };
  };

  // 获取筛选后的预订分析数据
  const getFilteredBookingData = () => {
    if (!alertData) return [];
    
    if (bookingRoomType === '全部房型') {
      // 如果选择全部房型，则需要合并相同日期的数据
      const dateMap = new Map();
      
      alertData.futureBookingData.forEach(item => {
        const dateKey = item.date;
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { 
            ...item, 
            bookingCount: item.bookingCount,
            // 价格和竞争价格取平均值
            price: item.price,
            competitorAvgPrice: item.competitorAvgPrice,
            count: 1
          });
        } else {
          const existing = dateMap.get(dateKey);
          existing.bookingCount += item.bookingCount;
          existing.price += item.price;
          existing.competitorAvgPrice += item.competitorAvgPrice || 0;
          existing.count += 1;
        }
      });
      
      // 计算平均值并返回按日期排序的数组
      return Array.from(dateMap.values())
        .map(item => ({
          ...item,
          bookingCount: Math.round(item.bookingCount / item.count),
          price: Math.round(item.price / item.count),
          competitorAvgPrice: Math.round((item.competitorAvgPrice || 0) / item.count)
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(0, 5); // 保持只显示5天数据
    } else {
      // 按房型筛选
      return alertData.futureBookingData
        .filter(item => item.roomType === bookingRoomType)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
  };

  // 修改预订分析图表配置
  const getBookingAnalysisOptions = () => {
    if (!alertData) return {};
    
    // 使用筛选后的预订数据
    const filteredData = getFilteredBookingData();
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: function(params: any) {
          let result = params[0].name + '<br/>';
          params.forEach((item: any) => {
            if (item.seriesName === '预订数量') {
              result += item.marker + ' ' + item.seriesName + ': ' + item.value + '间<br/>';
            } else {
              result += item.marker + ' ' + item.seriesName + ': ¥' + item.value + '<br/>';
            }
          });
          return result;
        }
      },
      legend: {
        data: ['预订数量', '本店房价', '竞争均价'],
        bottom: 0,
        textStyle: {
          fontSize: 10
        },
        itemWidth: 10,
        itemHeight: 10
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: filteredData.map(item => {
          // 将日期格式化为 "MM-DD" 格式
          const date = new Date(item.date);
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        }),
        axisLabel: {
          fontSize: 11,
          interval: 0,
          formatter: function(value: string, index: number) {
            const item = filteredData[index];
            if (item.isWarningDay) {
              return `{warningDay|${value}}`;
            } else if (item.isNotificationDay) {
              return `{notificationDay|${value}}`;
            }
            return value;
          },
          rich: {
            warningDay: {
              color: '#FF4D4F',
              fontWeight: 'bold',
              backgroundColor: '#FFF1F0',
              padding: [2, 4],
              borderRadius: 2
            },
            notificationDay: {
              color: '#1890FF',
              backgroundColor: '#E6F7FF',
              padding: [2, 4],
              borderRadius: 2
            }
          }
        }
      },
      yAxis: [
        {
          type: 'value',
          name: '预订数量',
          position: 'left',
          axisLine: {
            lineStyle: {
              color: '#5470C6'
            }
          },
          axisLabel: {
            fontSize: 10,
            formatter: '{value}间'
          }
        },
        {
          type: 'value',
          name: '房价',
          position: 'right',
          offset: 0,
          axisLine: {
            lineStyle: {
              color: '#EE6666'
            }
          },
          axisLabel: {
            fontSize: 10,
            formatter: '¥{value}'
          }
        }
      ],
      series: [
        {
          name: '预订数量',
          type: 'bar',
          data: filteredData.map((item, index) => {
            // 为预警日添加特殊样式
            if (item.isWarningDay) {
              return {
                value: item.bookingCount,
                itemStyle: {
                  color: '#5470C6',
                  borderWidth: 2,
                  borderColor: '#FF4D4F',
                  shadowBlur: 4,
                  shadowColor: 'rgba(255, 77, 79, 0.5)'
                }
              };
            }
            return item.bookingCount;
          }),
          barWidth: 10,
          itemStyle: {
            color: '#5470C6'
          },
          yAxisIndex: 0,
          markLine: {
            silent: true,
            data: [{
              xAxis: filteredData.findIndex(item => item.isWarningDay),
              lineStyle: {
                color: '#FF4D4F',
                type: 'dashed'
              },
              label: {
                formatter: '预警日',
                position: 'insideStartTop',
                color: '#FF4D4F'
              }
            }]
          }
        },
        {
          name: '本店房价',
          type: 'line',
          smooth: true,
          data: filteredData.map(item => item.price),
          symbolSize: 6,
          itemStyle: {
            color: '#EE6666'
          },
          lineStyle: {
            width: 2
          },
          yAxisIndex: 1
        },
        {
          name: '竞争均价',
          type: 'line',
          smooth: true,
          data: filteredData.map(item => item.competitorAvgPrice),
          symbolSize: 4,
          symbol: 'circle',
          itemStyle: {
            color: '#91CC75'
          },
          lineStyle: {
            width: 1.5,
            type: 'dashed'
          },
          yAxisIndex: 1
        }
      ]
    };
  };

  // Prepare tabs
  const tabItems: TabsProps['items'] = [
    {
      key: 'inventory',
      label: (
        <span>
          <BarChartOutlined />
          可售库存趋势
        </span>
      ),
      children: alertData && (
        <Card bodyStyle={{ padding: 10 }}>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>选择房型:</Text>
            <Select
              value={selectedRoomType}
              onChange={(value) => setSelectedRoomType(value)}
              style={{ width: '100%' }}
              options={alertData.availableRoomTypes.map(rt => ({
                value: rt.id,
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{rt.name}</span>
                    {rt.isWarning && (
                      <Tag color="#ff4d4f" style={{ marginLeft: 8, fontSize: 10 }}>
                        预警中
                      </Tag>
                    )}
                  </span>
                )
              }))}
              optionLabelProp="label"
            />
          </div>
          <ReactECharts 
            option={getInventoryOptions()} 
            style={{ height: 240 }} 
          />
          <Divider style={{ margin: '12px 0' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>各渠道库存锁定情况</Text>
          <List
            size="small"
            dataSource={alertData.channelData}
            renderItem={item => (
              <List.Item style={{ padding: '12px 0' }}>
                <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                  {/* 左侧渠道名称和图标 */}
                  <div style={{ display: 'flex', alignItems: 'center', width: '20%', marginRight: 10 }}>
                    <Avatar 
                      src={item.icon} 
                      size="small" 
                      style={{ 
                        backgroundColor: item.color,
                        marginRight: 8 
                      }}
                    />
                    <Text strong style={{ fontSize: 14 }}>{item.channel}</Text>
                  </div>
                  
                  {/* 右侧进度条和数字 */}
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: 4,
                      fontSize: 12
                    }}>
                      <Text type="secondary">已订: <Text strong>{item.booked}</Text></Text>
                      <Text type="secondary">可售: <Text strong>{item.availableToSell}</Text></Text>
                    </div>
                    
                    <Progress 
                      percent={Math.round((item.booked / (item.booked + item.availableToSell)) * 100)} 
                      size="small" 
                      showInfo={false}
                      strokeColor={item.color}
                    />
                  </div>
                </div>
              </List.Item>
            )}
          />
        </Card>
      )
    }
  ];

  // 获取事件类型图标和颜色
  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'holiday':
        return { icon: <CalendarOutlined />, color: '#1890ff', text: '假期' };
      case 'exhibition':
        return { icon: <ShopOutlined />, color: '#52c41a', text: '会展' };
      case 'festival':
        return { icon: <SoundOutlined />, color: '#faad14', text: '节日' };
      case 'other':
        return { icon: <TagOutlined />, color: '#722ed1', text: '其他' };
      default:
        return { icon: <InfoCircleOutlined />, color: '#8c8c8c', text: '其他' };
    }
  };

  // 获取影响程度标签颜色
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return '#ff4d4f';
      case 'medium':
        return '#faad14';
      case 'low':
        return '#52c41a';
      default:
        return '#8c8c8c';
    }
  };

  // 检查事件是否包含预警日期
  const isEventCoveringWarningDate = (startDate: string, endDate: string, warningDate: string) => {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const warning = new Date(warningDate).getTime();
    return start <= warning && warning <= end;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!alertData) {
    return (
      <div style={{ padding: 16, textAlign: 'center' }}>
        <Text>预警数据不存在或已过期</Text>
        <Button type="primary" onClick={() => navigate(-1)}>返回</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 500, margin: '0 auto', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* 顶部固定部分 */}
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space align="center">
            <ExclamationCircleOutlined 
              style={{ 
                fontSize: 24, 
                color: getSeverityColor(alertData.severity)
              }} 
            />
            <Title level={4} style={{ margin: 0 }}>库存预警</Title>
          </Space>
          <Space>
            <Tooltip title="刷新数据">
              <Button 
                type="text" 
                icon={<SyncOutlined spin={refreshing} />} 
                onClick={handleRefresh}
              />
            </Tooltip>
            <Text type="secondary" style={{ fontSize: 12 }}>
              更新于 {alertData.lastUpdated}
            </Text>
          </Space>
        </div>
      </Card>
      
      {/* A. 预警核心信息区 */}
      <Card 
        style={{ 
          marginBottom: 16, 
          borderRadius: 8, 
          borderLeft: `4px solid ${getSeverityColor(alertData.severity)}`,
          backgroundColor: alertData.severity === 'high' ? '#FFF1F0' : (
            alertData.severity === 'medium' ? '#FFFBE6' : '#F6FFED'
          )
        }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {/* 每条数据单独一行 */}
          <div style={{ padding: '4px 0' }}>
            <Text>预警日期: {alertData.date}</Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>门店名称: {alertData.storeName}</Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>房型: {alertData.roomType}</Text>
          </div>
          
          {/* 添加通知内容 */}
          <div style={{ padding: '4px 0' }}>
            <Text>通知内容: <Text type="warning" strong>{alertData.notificationContent}</Text></Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>通知时间: {alertData.notificationTime}</Text>
          </div>
          
          <Divider style={{ margin: '8px 0' }} />
          
          {/* 库存剩余 - 带对比数据 */}
          <div style={{ padding: '8px 0' }}>
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 15 }}>库存剩余</Text>
            </div>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic 
                    value={Math.round(alertData.remainingInventory)} 
                    suffix="间"
                    valueStyle={{ 
                      color: getSeverityColor(alertData.severity),
                      fontSize: 24
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>当前</Text>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic 
                    value={alertData.comparisonData.lastWeek.remainingInventory} 
                    suffix="间"
                    valueStyle={{ 
                      fontSize: 18
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>上周同期</Text>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic 
                    value={alertData.comparisonData.competitorAverage.remainingInventory} 
                    suffix="间"
                    valueStyle={{ 
                      fontSize: 18
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>竞争圈均值</Text>
                </div>
              </Col>
            </Row>
          </div>
          
          {/* 已预订 - 带对比数据 */}
          <div style={{ padding: '8px 0' }}>
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 15 }}>已预订</Text>
            </div>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic 
                    value={alertData.bookedInventory} 
                    suffix="间"
                    valueStyle={{ 
                      fontSize: 24
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>当前</Text>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic 
                    value={alertData.comparisonData.lastWeek.bookedInventory} 
                    suffix="间"
                    valueStyle={{ 
                      fontSize: 18
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>上周同期</Text>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic 
                    value={alertData.comparisonData.competitorAverage.bookedInventory} 
                    suffix="间"
                    valueStyle={{ 
                      fontSize: 18
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>竞争圈均值</Text>
                </div>
              </Col>
            </Row>
          </div>
          
          {/* 删除总房量部分和风险提示文字与进度圆环 */}
          
        </Space>
      </Card>
      
      {/* C. 智能建议区 */}
      <Card 
        title={
          <Space>
            <InfoCircleOutlined />
            <span>智能建议</span>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 8 }}
        headStyle={{ padding: '0 12px' }}
        bodyStyle={{ padding: 12 }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card
            style={{ 
              backgroundColor: '#E6F7FF',
              borderColor: '#91D5FF'
            }}
            bodyStyle={{ padding: 12 }}
            size="small"
          >
            <Text strong>{alertData.recommendedAction}</Text>
            <div style={{ marginTop: 12 }}>
              {alertData.recommendationReason.split('\n').map((item, index) => (
                <div key={index} style={{ 
                  marginBottom: 8, 
                  paddingLeft: 4,
                  lineHeight: '1.5',
                  color: 'rgba(0, 0, 0, 0.65)',
                  fontSize: 13
                }}>
                  {item}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#8C8C8C', marginTop: 4 }}>
              建议生成时间: {alertData.lastUpdated}
            </div>
          </Card>
          
          <Row gutter={8}>
            <Col span={12}>
              <Button 
                type="primary" 
                block
                icon={<SettingOutlined />}
                onClick={handleGoToSettings}
              >
                去调价
              </Button>
            </Col>
            <Col span={12}>
              {/* 移除配置通知按钮 */}
            </Col>
          </Row>
        </Space>
      </Card>
      
      {/* B. 库存和订单综合分布 */}
      <Tabs 
        items={tabItems}  // 移除slice，因为现在tabItems只有一项
        defaultActiveKey="inventory"
        style={{ 
          marginBottom: 16,
          background: '#fff',
          borderRadius: 8,
          padding: '8px 8px 0 8px'
        }}
      />
      
      {/* 预订分析模块 */}
      <Card
        title={
          <Space>
            <LineChartOutlined />
            <span>预订与房价走势</span>
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                  从通知日到预警日后2天
                </Text>
          </Space>
        }
        style={{ 
          marginBottom: 16, 
          borderRadius: 8
        }}
        headStyle={{ padding: '0 12px' }}
        bodyStyle={{ padding: 12 }}
      >
        {alertData && (
          <>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              
              
              <Select
                value={bookingRoomType}
                onChange={(value) => setBookingRoomType(value)}
                style={{ width: 180 }}
                options={[
                  { value: '全部房型', label: '全部房型' },
                  { value: alertData.roomType, label: (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {alertData.roomType}
                      <Tag color="#ff4d4f" style={{ marginLeft: 4, fontSize: 10 }}>
                        预警中
                      </Tag>
                    </span>
                  )},
                  ...alertData.availableRoomTypes
                    .filter(rt => rt.name !== alertData.roomType)
                    .map(rt => ({ value: rt.name, label: rt.name }))
                ]}
                size="small"
              />
            </div>
            <ReactECharts 
              option={getBookingAnalysisOptions()} 
              style={{ height: 240 }} 
            />
            <div style={{ marginTop: 10 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <InfoCircleOutlined style={{ marginRight: 5 }} />
                7月1日预订量达到峰值，建议重点关注此期间超卖风险
              </Text>
            </div>
          </>
        )}
      </Card>
      
      {/* 竞品分析 - 单独展示 */}
      <Card
        title={
          <Space>
            <LineChartOutlined />
            <span>竞品分析</span>
          </Space>
        }
        style={{ 
          marginBottom: 16, 
          borderRadius: 8
        }}
        headStyle={{ padding: '0 12px' }}
        bodyStyle={{ padding: 12 }}
      >
        {alertData && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>选择房型:</Text>
                <Select
                  value={competitorRoomType}
                  onChange={(value) => setCompetitorRoomType(value)}
                  style={{ width: 180 }}
                  options={[
                    { value: '全部房型', label: '全部房型' },
                    { value: alertData.roomType, label: (
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {alertData.roomType}
                        <Tag color="#ff4d4f" style={{ marginLeft: 4, fontSize: 10 }}>
                          预警中
                        </Tag>
                      </span>
                    )},
                    ...alertData.availableRoomTypes
                      .filter(rt => rt.name !== alertData.roomType)
                      .map(rt => ({ value: rt.name, label: rt.name }))
                  ]}
                  size="small"
                />
              </Space>
            </div>
            <ReactECharts 
              option={getCompetitorOptions()} 
              style={{ height: 240 }} 
            />
            <Divider style={{ margin: '12px 0' }} />
            <Table
              size="small"
              dataSource={getFilteredCompetitorData().map((item, index) => ({
                ...item,
                key: index,
              }))}
              pagination={false}
              columns={[
                {
                  title: '酒店名称',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text) => <Text style={{ fontSize: 13 }}>{text}</Text>
                },
                {
                  title: '房价',
                  dataIndex: 'price',
                  key: 'price',
                  render: (price) => (
                    <Text type="danger" style={{ fontSize: 13 }}>¥{price}</Text>
                  ),
                  align: 'center',
                  width: 70
                },
                {
                  title: '剩余',
                  dataIndex: 'remainingRooms',
                  key: 'remainingRooms',
                  render: (rooms) => (
                    <Text type="warning" style={{ fontSize: 13 }}>{rooms}间</Text>
                  ),
                  align: 'center',
                  width: 70
                }
              ]}
              summary={() => (
                <Table.Summary.Row style={{ backgroundColor: '#f0f5ff' }}>
                  <Table.Summary.Cell index={0}><Text strong>本酒店</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <Text type="danger" strong>¥1180</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <Text type="warning" strong>{Math.round(alertData.remainingInventory)}间</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
            <div style={{ fontSize: 12, color: '#8C8C8C', marginTop: 8, textAlign: 'right' }}>
              数据更新于: {alertData.lastUpdated}
            </div>
          </>
        )}
      </Card>
      
      {/* 事件信息模块 */}
      <Card
        title={
          <Space>
            <CalendarOutlined />
            <span>事件信息</span>
          </Space>
        }
        style={{ 
          marginBottom: 16, 
          borderRadius: 8
        }}
        headStyle={{ padding: '0 12px' }}
        bodyStyle={{ padding: 12 }}
      >
        {alertData && (
          <>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {alertData.events.map(event => (
                <Card 
                  key={event.id}
                  size="small"
                  style={{ 
                    borderLeft: `3px solid ${getEventTypeIcon(event.type).color}`,
                    backgroundColor: '#fafafa'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Space>
                      <Text strong>{event.name}</Text>
                      <Tag color={getEventTypeIcon(event.type).color}>
                        {event.type === 'holiday' ? '节假日' : 
                         event.type === 'exhibition' ? '展会' : 
                         event.type === 'festival' ? '节日' : '其他'}
                      </Tag>
                    </Space>
                    <Tag color={getImpactColor(event.impact)}>
                      影响度: {event.impact === 'high' ? '高' : event.impact === 'medium' ? '中' : '低'}
                    </Tag>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">日期: {dayjs(event.startDate).format('YYYY-MM-DD')}
                    {event.startDate !== event.endDate ? ` 至 ${dayjs(event.endDate).format('YYYY-MM-DD')}` : ''}
                    </Text>
                  </div>
                  <div>
                    <Text>{`${event.name}期间客流量可能${event.impact === 'high' ? '大幅' : event.impact === 'medium' ? '中度' : '略微'}增加，建议关注房价和库存情况`}</Text>
                  </div>
                </Card>
              ))}
            </Space>
            <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
              <Text type="secondary">* 事件可能对入住率和房价产生显著影响，建议结合事件进行分析和决策</Text>
            </div>
          </>
        )}
      </Card>
      
      <div style={{ height: 40 }} />
    </div>
  );
};

export default InventoryAlertAnalysis; 